import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { client, NewCommentFile, FileType, InsuranceOption, OutboundTaskType, DocumentUploadTask } from '../api';
import { Sidebar, SidebarTopBar } from '../components/sidebar/Sidebar';
import { useContext, useState } from 'react';
import { DrugPanel, PatientPanel, PharmacyPanel, PrescriberPanel } from '../components/sidebar/SidebarPanels';
import styled from 'styled-components';
import { TaskContext } from '../components/tasks/contexts';
import Button from 'design-system/src/components/Button/Button';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { ItemViewTopNav } from '../components/tableItemDetailViewComponents/ItemViewTopNav';
import {
  BodyContainer,
  TopNavAndContent,
  ContentOuterWrapper,
  CenteredContent,
} from '../components/tableItemDetailViewComponents/Containers';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'design-system/src/components/Badge/Badge';
import { FileUploader, UploadedFileRow, UploadedFile } from '../components/tasks/Files';
import { GeneralInfoTable } from '../components/GeneralInfoTable/GeneralInfoTable';
import DeclineModal from '../components/tasks/DeclineModal';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { InstructionsContainer } from '../components/tasks/TaskView';
import { Dropdown, DropdownItem, DropdownContainer } from '../components/Dropdown.tsx';
import { FormLabel } from '../components/FormLabel';
import { AttachedFile } from '../components/tasks/Files';
import NewFollowupTaskModal from '../components/tasks/NewFollowupTaskModal';
import { StructuredTaskSmallPrint } from '../components/tasks/StructuredTaskSmallPrint/StructuredTaskSmallPrint.tsx';
import { createTrackingProps } from 'shared-utils/analytics';

const DocumentSection = styled.div`
  display: flex;
  flex-direction: row;
  padding: 16px 0;
  border-bottom: 1px solid var(--border-gray);
`;

const DocumentSectionTitle = styled.div`
  min-width: 200px;
`;

const DocumentContainer = styled.div`
  border: 1px solid var(--border-gray);
  border-radius: var(--border-radius-medium);
  padding: 16px;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DocumentContainerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  height: 100%;
`;

const FixedWidthDropdownContainer = styled(DropdownContainer)`
  width: 300px;
`;

const EventFiles = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const InsuranceCardSection: React.FC<{
  insuranceOptionSelected: InsuranceOption;
  setInsuranceOption: (insuranceOption: InsuranceOption) => void;
  isTaskCompleted: boolean;
  showInsuranceOnFile: boolean;
  insuranceCardFiles: NewCommentFile[];
  handleFileDelete: (filename: string, document_type: FileType) => void;
  handleFileUpload: (newFiles: NewCommentFile[]) => void;
  uploadedFileIds: string[];
}> = ({
  insuranceOptionSelected,
  setInsuranceOption,
  isTaskCompleted,
  showInsuranceOnFile,
  insuranceCardFiles,
  handleFileDelete,
  handleFileUpload,
  uploadedFileIds,
}) => {
  return (
    <DocumentContainerContent>
      <FixedWidthDropdownContainer>
        <FormLabel id="insurance-option-label">Insurance option</FormLabel>
        <Dropdown
          labelId="insurance-option-label"
          id="insurance-option"
          value={insuranceOptionSelected}
          onChange={(event) => {
            setInsuranceOption(event.target.value as InsuranceOption);
          }}
          disabled={isTaskCompleted}
        >
          {showInsuranceOnFile && (
            <DropdownItem value="use_existing_insurance">Use existing insurance on file</DropdownItem>
          )}
          <DropdownItem value="new_insurance">Upload new insurance</DropdownItem>
          <DropdownItem value="no_insurance">Patient is self pay / uninsured</DropdownItem>
          <DropdownItem value="insurance_unavailable">Insurance not available</DropdownItem>
        </Dropdown>
      </FixedWidthDropdownContainer>

      {insuranceOptionSelected === InsuranceOption.NewInsurance && (
        <>
          {isTaskCompleted ? (
            uploadedFileIds.length > 0 ? (
              <EventFiles>
                {uploadedFileIds.map((fileId: string) => (
                  <AttachedFile key={fileId} fileId={fileId} />
                ))}
              </EventFiles>
            ) : (
              <Typography styledAs="bodySmallDMSans">No insurance card uploaded</Typography>
            )
          ) : (
            <DocumentContainerContent>
              <Typography styledAs="bodySmallDMSans" weight="bold" marginTop="16px">
                Please upload an image of the front and back of the card.
              </Typography>
              {insuranceCardFiles.length > 0 && (
                <UploadedFileRow>
                  {insuranceCardFiles.map((fileInfo) => (
                    <UploadedFile
                      key={`${fileInfo.file.name}-${fileInfo.document_type}`}
                      filename={fileInfo.file.name}
                      fileType={fileInfo.document_type}
                      onFileDelete={handleFileDelete}
                    />
                  ))}
                </UploadedFileRow>
              )}
              <FileUploader
                onFilesUpload={handleFileUpload}
                fileTypesOptions={['Insurance Card Both Sides']}
                showAttachButtonLabel
              />
            </DocumentContainerContent>
          )}
        </>
      )}
    </DocumentContainerContent>
  );
};

const MedicalRecordsSection: React.FC<{
  files: NewCommentFile[];
  handleFileDelete: (filename: string, document_type: FileType) => void;
  handleFileUpload: (newFiles: NewCommentFile[]) => void;
  isTaskCompleted: boolean;
  uploadedFileIds: string[];
}> = ({ files, handleFileDelete, handleFileUpload, isTaskCompleted, uploadedFileIds }) => {
  if (isTaskCompleted) {
    return uploadedFileIds.length > 0 ? (
      <EventFiles>
        {uploadedFileIds.map((fileId) => (
          <AttachedFile key={fileId} fileId={fileId} />
        ))}
      </EventFiles>
    ) : (
      <Typography styledAs="bodySmallDMSans">No medical records uploaded</Typography>
    );
  }

  return (
    <DocumentContainerContent>
      <Typography styledAs="bodySmallDMSans" weight="bold">
        Please upload visit notes and any supporting clinical documentation (e.g. lab results) from the patient's most
        recent appointment related to treatment.
      </Typography>
      {files.length > 0 && (
        <UploadedFileRow>
          {files.map((fileInfo) => (
            <UploadedFile
              key={`${fileInfo.file.name}-${fileInfo.document_type}`}
              filename={fileInfo.file.name}
              fileType={fileInfo.document_type}
              onFileDelete={handleFileDelete}
            />
          ))}
        </UploadedFileRow>
      )}
      <FileUploader onFilesUpload={handleFileUpload} fileTypesOptions={['Medical Records']} showAttachButtonLabel />
    </DocumentContainerContent>
  );
};

const DocumentUploadTaskView: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);
  const [showHelpRequestModal, setShowHelpRequestModal] = useState(false);
  const [files, setFiles] = useState<NewCommentFile[]>([]);
  const [insuranceOption, setInsuranceOption] = useState<InsuranceOption | undefined>();

  const { taskQuery, taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();

  const submit = useMutation<void, Error, InsuranceOption>({
    mutationFn: async (insuranceOptionValue: InsuranceOption) => {
      const formData = new FormData();

      files.forEach((fileInfo) => {
        if (fileInfo.document_type === 'Insurance Card Both Sides') {
          formData.append('insurance_card_files[]', fileInfo.file);
        } else if (fileInfo.document_type === 'Medical Records') {
          formData.append('medical_record_files[]', fileInfo.file);
        }
      });

      formData.append('insurance_option', insuranceOptionValue);
      return await client.post(`tasks/${taskId}/submit-documents-requested`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      navigate('/tasks');
      toast.custom(() => <Toast variant="success">Document upload task submitted successfully</Toast>);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const handleFileUpload = (newFiles: NewCommentFile[]) => {
    setFiles([...files, ...newFiles]);
  };

  const handleFileDelete = (filename: string, document_type: FileType) => {
    const updatedFiles = files.filter(
      (fileInfo) => !(fileInfo.file.name === filename && fileInfo.document_type === document_type),
    );
    setFiles(updatedFiles);
  };

  let body;
  let declineModal;
  let helpRequestModal;
  if (taskQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (taskQuery.error) {
    body = <PageLoadError />;
  } else if (taskQuery.data) {
    const { drug, patient, prescriber, pharmacy, show_update_pharmacy_button } = taskQuery.data;
    const task = taskQuery.data.task as DocumentUploadTask;
    const showInsuranceOnFile = task.show_insurance_on_file ?? false;
    const isTaskCompleted = task.status === 'completed';

    declineModal = (
      <DeclineModal
        closeModal={() => setShowCancelConfirmationModal(false)}
        patient={patient}
        drug={drug}
        taskType={task.type}
        taskId={taskId}
        onTaskUpdate={onTaskUpdate}
      />
    );

    helpRequestModal = (
      <NewFollowupTaskModal
        closeModal={() => setShowHelpRequestModal(false)}
        patient={patient}
        drug={drug}
        patientId={patient.id}
        prescriptionId={drug.id}
        taskId={taskId}
        relatedTaskType={task.type as OutboundTaskType}
      />
    );

    const insuranceCardFiles = files.filter((file) => file.document_type === 'Insurance Card Both Sides');
    const medicalRecordsFiles = files.filter((file) => file.document_type === 'Medical Records');

    let insuranceOptionSelected: InsuranceOption;
    if (isTaskCompleted) {
      // Set insurance option to the value selected in the task response
      insuranceOptionSelected = task.insurance_option;
    } else {
      // Set insurance option to the selected value or the right default value
      const defaultInsuranceOption = showInsuranceOnFile ? 'use_existing_insurance' : 'new_insurance';
      insuranceOptionSelected = (insuranceOption ?? defaultInsuranceOption) as InsuranceOption;
    }

    body = (
      <BodyContainer>
        <TopNavAndContent>
          <ItemViewTopNav
            isSidebarCollapsed={isSidebarCollapsed}
            setIsSidebarCollapsed={setIsSidebarCollapsed}
            additionalRightSideContent={isTaskCompleted && <Badge variant="neutral">Closed</Badge>}
          />

          <ContentOuterWrapper>
            <CenteredContent>
              <Typography styledAs="h6" marginBottom="1rem">
                {task.type}
              </Typography>
              <GeneralInfoTable
                patientName={drug.patient_name}
                prescriberName={drug.prescriber_name}
                dob={drug.dob}
                prescribedOn={drug.prescription_recieved_at}
                drugName={drug.name}
                prescriptionId={drug.id}
                relatedPrescriptions={task.related_prescriptions}
              />

              <>
                {!isTaskCompleted && (
                  <InstructionsContainer>
                    <Typography weight="bold">Please upload the relevant patient documentation.</Typography>
                  </InstructionsContainer>
                )}

                <DocumentContainer>
                  <DocumentSection>
                    <DocumentSectionTitle>
                      <Typography id="insurance-option-label">Insurance card</Typography>
                    </DocumentSectionTitle>
                    <InsuranceCardSection
                      insuranceOptionSelected={insuranceOptionSelected as InsuranceOption}
                      setInsuranceOption={setInsuranceOption}
                      isTaskCompleted={isTaskCompleted}
                      showInsuranceOnFile={showInsuranceOnFile}
                      insuranceCardFiles={insuranceCardFiles}
                      handleFileDelete={handleFileDelete}
                      handleFileUpload={handleFileUpload}
                      uploadedFileIds={task.insurance_card_document_ids ?? []}
                    />
                  </DocumentSection>

                  <DocumentSection>
                    <DocumentSectionTitle>
                      <Typography>Medical records</Typography>
                    </DocumentSectionTitle>
                    <MedicalRecordsSection
                      files={medicalRecordsFiles}
                      handleFileDelete={handleFileDelete}
                      handleFileUpload={handleFileUpload}
                      isTaskCompleted={isTaskCompleted}
                      uploadedFileIds={task.medical_records_document_ids ?? []}
                    />
                  </DocumentSection>
                  {!isTaskCompleted && (
                    <ButtonRow align={Alignment.SpaceBetween}>
                      <ButtonRow>
                        <Button
                          {...createTrackingProps({
                            elementName: 'Document Upload Task Page: Cancel Prescription Button',
                          })}
                          variant="secondary"
                          onClick={() => setShowCancelConfirmationModal(true)}
                          size="sm"
                          disabled={submit.isPending}
                        >
                          Cancel prescription
                        </Button>
                        <Button
                          {...createTrackingProps({ elementName: 'Document Upload Task Page: Help Request Button' })}
                          variant="link"
                          size="sm"
                          onClick={() => setShowHelpRequestModal(true)}
                          disabled={submit.isPending}
                        >
                          Something else?
                        </Button>
                      </ButtonRow>
                      <Button
                        {...createTrackingProps({ elementName: 'Document Upload Task Page: Submit Button' })}
                        variant="primary"
                        onClick={() => submit.mutate(insuranceOptionSelected as InsuranceOption)}
                        size="sm"
                        disabled={
                          submit.isPending ||
                          (insuranceOption === InsuranceOption.NewInsurance && insuranceCardFiles.length === 0) ||
                          !insuranceOptionSelected
                        }
                      >
                        Submit and close task
                      </Button>
                    </ButtonRow>
                  )}
                </DocumentContainer>
              </>

              <StructuredTaskSmallPrint />
            </CenteredContent>
          </ContentOuterWrapper>
        </TopNavAndContent>

        {!isSidebarCollapsed && (
          <Sidebar>
            <SidebarTopBar />
            <DrugPanel drug={drug} />
            <PharmacyPanel
              pharmacy={pharmacy}
              showUpdatePharmacyButton={show_update_pharmacy_button}
              rxId={drug.id}
              onPharmacyUpdate={() => onTaskUpdate()}
            />
            <PatientPanel patient={patient} />
            <PrescriberPanel prescriber={prescriber} />
          </Sidebar>
        )}
      </BodyContainer>
    );
  }

  return (
    <>
      {body}
      {showCancelConfirmationModal && declineModal}
      {showHelpRequestModal && helpRequestModal}
    </>
  );
};

export default DocumentUploadTaskView;
